import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Download, 
  Filter, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  FileText,
  Users,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { obtenerFacturas, obtenerMovimientos, eliminarMovimiento, obtenerUrlArchivo } from '../servicios/api';
import ModalCrearGasto from '../componentes/ModalCrearGasto';
import ModalCrearIngreso from '../componentes/ModalCrearIngreso';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const FinancialApp = () => {
  const [currentSection, setCurrentSection] = useState('ingresos');
  const [errorApi, setErrorApi] = useState(null);
  const [isGastoModalOpen, setIsGastoModalOpen] = useState(false);
  const [isIngresoModalOpen, setIsIngresoModalOpen] = useState(false);
  const [movimientoAEditar, setMovimientoAEditar] = useState(null); // Nuevo estado para el movimiento a editar

  const [ingresosData, setIngresosData] = useState([]);
  const [gastosData, setGastosData] = useState([]);

  const getToday = () => new Date();
  const formatDate = (date) => date.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(() => {
    const today = getToday();
    return formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    const today = getToday();
    return formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  });

  const cargarIngresos = async () => {
    try {
      const [facturasResponse, movimientosResponse] = await Promise.all([
        obtenerFacturas(),
        obtenerMovimientos()
      ]);

      let ingresosDesdeFacturas = [];
      if (facturasResponse.exito && Array.isArray(facturasResponse.facturas)) {
        ingresosDesdeFacturas = facturasResponse.facturas.map(factura => ({
          id: `factura-${factura.id_factura}`,
          tipo: 'factura',
          fecha: factura.fecha_emision,
          cliente: factura.nombre_cliente || 'Desconocido',
          contacto: factura.contacto_cliente || '',
          concepto: `Factura #${factura.numero_factura}`,
          monto: parseFloat(factura.total),
          tecnico: factura.nombre_tecnico || 'Desconocido',
          estado: factura.estado_pago || 'pendiente',
          justificante: null,
        }));
      }

      let ingresosDesdeMovimientos = [];
      if (movimientosResponse.exito && Array.isArray(movimientosResponse.movimientos)) {
        ingresosDesdeMovimientos = movimientosResponse.movimientos
          .filter(mov => mov.tipo_movimiento === 'ingreso')
          .map(ingresoMov => ({
            id: `movimiento-${ingresoMov.id_movimiento}`,
            id_movimiento: ingresoMov.id_movimiento, // Preserve original ID for deletion
            tipo: 'movimiento',
            fecha: ingresoMov.fecha,
            cliente: ingresoMov.cliente || 'N/A',
            contacto: '',
            concepto: ingresoMov.concepto,
            monto: parseFloat(ingresoMov.monto),
            tecnico: ingresoMov.responsable || 'N/A',
            estado: 'registrado',
            justificante: ingresoMov.justificante,
          }));
      }

      const todosLosIngresos = [...ingresosDesdeFacturas, ...ingresosDesdeMovimientos];
      todosLosIngresos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

      setIngresosData(todosLosIngresos);

    } catch (error) {
      console.error("Error al cargar los ingresos:", error);
      setErrorApi("Error de conexión con el servidor al cargar ingresos. Inténtalo de nuevo más tarde.");
      setIngresosData([]);
    }
  };

  const cargarGastos = async () => {
    try {
      const response = await obtenerMovimientos();
      if (response.exito && Array.isArray(response.movimientos)) {
        const gastosFiltrados = response.movimientos.filter(mov => mov.tipo_movimiento === 'gasto');
        setGastosData(gastosFiltrados);
      } else {
        setGastosData([]);
      }
    } catch (error) {
      console.error("Error al cargar los gastos:", error.response ? error.response.data : error);
      setErrorApi(error.response?.data?.mensaje ? `Error del servidor: ${error.response.data.mensaje}` : "Error de conexión con el servidor.");
      setGastosData([]);
    }
  };

  const handleEliminarMovimiento = async (idMovimiento, tipo) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar este ${tipo}? Esta acción no se puede deshacer.`)) {
      try {
        const response = await eliminarMovimiento(idMovimiento);
        if (response.exito) {
          alert(response.mensaje);
          cargarIngresos();
          cargarGastos();
        } else {
          alert(`Error: ${response.mensaje}`);
        }
      } catch (error) {
        console.error('Error al eliminar movimiento:', error);
        alert('Se produjo un error en el servidor al intentar eliminar el movimiento.');
      }
    }
  };

  const handleEditClick = (movimiento) => {
    setMovimientoAEditar(movimiento);
    if (movimiento.tipo === 'ingreso') {
      setIsIngresoModalOpen(true);
    } else if (movimiento.tipo === 'gasto') {
      setIsGastoModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsIngresoModalOpen(false);
    setIsGastoModalOpen(false);
    setMovimientoAEditar(null); // Limpiar el movimiento a editar al cerrar el modal
  };

  useEffect(() => {
    cargarIngresos();
    cargarGastos();
  }, []);

  const filteredIngresos = ingresosData.filter(ingreso => {
    const ingresoDate = new Date(ingreso.fecha);
    return ingresoDate >= new Date(startDate) && ingresoDate <= new Date(endDate);
  });

  const filteredGastos = gastosData.filter(gasto => {
    const gastoDate = new Date(gasto.fecha);
    return gastoDate >= new Date(startDate) && gastoDate <= new Date(endDate);
  });

  const totalIngresos = filteredIngresos.reduce((sum, item) => sum + (item.estado !== 'anulado' ? item.monto : 0), 0);
  const totalGastos = filteredGastos.reduce((sum, item) => sum + parseFloat(item.monto), 0);
  const balance = totalIngresos - totalGastos;

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'cobrado': return 'text-green-400 bg-green-500/20';
      case 'pendiente': return 'text-yellow-400 bg-yellow-500/20';
      case 'anulado': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      materiales: '#3b82f6',
      combustible: '#f97316',
      alquiler: '#8b5cf6',
      herramientas: '#22c55e',
      oficina: '#6366f1',
      servicios: '#ec4899',
      salarios: '#14b8a6',
      transporte: '#84cc16',
      marketing: '#06b6d4',
      otros: '#6b7280',
    };
    return colors[categoria?.toLowerCase()] || '#6b7280';
  };

  const NavButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setCurrentSection(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
        currentSection === id
          ? 'bg-blue-600 text-white'
          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );

  const IngresosSection = () => (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-100">Ingresos</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => setIsIngresoModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400">
              <Plus size={16} />
              Añadir Ingreso
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-500/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-green-300 mb-1">
              <TrendingUp size={20} />
              <span className="font-semibold">Total Ingresos</span>
            </div>
            <p className="text-2xl font-bold text-green-300">€{totalIngresos.toFixed(2)}</p>
          </div>
          <div className="bg-blue-500/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-blue-300 mb-1">
              <Users size={20} />
              <span className="font-semibold">Vs Mes Anterior</span>
            </div>
            <p className="text-2xl font-bold text-blue-300">&#43;12.5%</p>
          </div>
          <div className="bg-orange-500/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-orange-300 mb-1">
              <Users size={20} />
              <span className="font-semibold">Clientes Atendidos</span>
            </div>
            <p className="text-2xl font-bold text-orange-300">{filteredIngresos.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <Filter size={16} className="text-gray-400" />
          <select className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-900 text-gray-300">
            <option>Todos los técnicos</option>
            <option>Carlos López</option>
            <option>Ana Martín</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-400">
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-gray-100">Detalle de Ingresos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Fecha</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Concepto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Monto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Técnico</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Acciones</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Justificante</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredIngresos.length > 0 ? (
                filteredIngresos.map((ingreso) => (
                  <tr key={ingreso.id} className="hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-100">{ingreso.fecha}</td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-100">{ingreso.cliente}</div>
                        <div className="text-sm text-gray-400">{ingreso.contacto}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-100">{ingreso.concepto}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-100">€{ingreso.monto.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-gray-100">{ingreso.tecnico}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ingreso.estado)}`}>
                        {ingreso.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => alert(`Ver detalle del ingreso: ${ingreso.concepto}`)} className="p-1 text-blue-400 hover:bg-blue-500/20 rounded"><Eye size={14} /></button>
                        <button 
                          onClick={() => {
                            if (ingreso.tipo !== 'factura') {
                              handleEditClick(ingreso);
                            } else {
                              alert('Las facturas se gestionan desde la sección de Facturación.');
                            }
                          }} 
                          disabled={ingreso.tipo === 'factura'}
                          className="p-1 text-green-400 hover:bg-green-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit size={14} />
                        </button>
                        <button 
                          onClick={() => {
                            if (ingreso.tipo !== 'factura') {
                              handleEliminarMovimiento(ingreso.id_movimiento, 'ingreso');
                            } else {
                              alert('Las facturas se gestionan desde la sección de Facturación.');
                            }
                          }} 
                          disabled={ingreso.tipo === 'factura'}
                          className="p-1 text-red-400 hover:bg-red-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {ingreso.justificante ? (
                        <a 
                          href={obtenerUrlArchivo(`uploads/movimientos/${ingreso.justificante}`)} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-400 hover:text-blue-300 text-sm underline"
                        >
                          Ver Justificante
                        </a>
                      ) : (
                        <span className="text-gray-500 text-sm">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-10 text-gray-400">No hay ingresos para mostrar en el rango de fechas seleccionado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const GastosSection = () => (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-100">Gastos</h2>
          <div className="flex flex-wrap gap-3">
            <button
             onClick={() => {
                setIsGastoModalOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-400"
            >
              <Plus size={16} />
              Añadir Gasto
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-500/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <TrendingDown size={20} />
              <span className="font-semibold">Total Gastos</span>
            </div>
            <p className="text-2xl font-bold text-red-400">€{totalGastos.toFixed(2)}</p>
          </div>
          <div className={`p-4 rounded-lg ${balance >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
            <div className={`flex items-center gap-2 mb-1 ${balance >= 0 ? 'text-green-300' : 'text-red-400'}`}>
              <DollarSign size={20} />
              <span className="font-semibold">Balance</span>
            </div>
            <p className={`text-2xl font-bold ${balance >= 0 ? 'text-green-300' : 'text-red-400'}`}>
              €{balance.toFixed(2)}
            </p>
          </div>
          <div className="bg-orange-500/20 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-orange-300 mb-1">
              <FileText size={20} />
              <span className="font-semibold">Documentos</span>
            </div>
            <p className="text-2xl font-bold text-orange-300">{filteredGastos.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <Filter size={16} className="text-gray-400" />
          <select className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-900 text-gray-300">
            <option>Todas las categorías</option>
            <option>Materiales</option>
            <option>Combustible</option>
            <option>Alquiler</option>
            <option>Herramientas</option>
          </select>
          <select className="px-3 py-2 border border-gray-700 rounded-lg bg-gray-900 text-gray-300">
            <option>Todos los proveedores</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-400">
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-gray-100">Detalle de Gastos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Fecha</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Proveedor</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Categoría</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Concepto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Monto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Responsable</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Justificante</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredGastos.length > 0 ? (
                filteredGastos.map((gasto) => (
                  <tr key={gasto.id_movimiento} className="hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-100">{gasto.fecha}</td>
                    <td className="px-4 py-3 text-sm text-gray-100">{gasto.proveedor}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(gasto.categoria)}`}>
                        {gasto.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-100">{gasto.concepto}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-400">€{gasto.monto.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-gray-100">{gasto.responsable}</td>
                    <td className="px-4 py-3">
                      {gasto.justificante ? (
                        <a href={obtenerUrlArchivo(`uploads/movimientos/${gasto.justificante}`)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm underline">Ver Documento</a>
                      ) : (
                        <span className="text-gray-500 text-sm">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => alert(`Ver detalle del gasto: ${gasto.concepto}`)} className="p-1 text-blue-400 hover:bg-blue-500/20 rounded"><Eye size={14} /></button>
                        <button onClick={() => handleEditClick(gasto)} className="p-1 text-green-400 hover:bg-green-500/20 rounded"><Edit size={14} /></button>
                        <button onClick={() => handleEliminarMovimiento(gasto.id_movimiento, 'gasto')} className="p-1 text-red-400 hover:bg-red-500/20 rounded"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-10 text-gray-400">No hay gastos para mostrar en el rango de fechas seleccionado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ModalCrearGasto 
        isOpen={isGastoModalOpen} 
        onClose={handleCloseModal} 
        onGastoCreado={cargarGastos}
        gastoAEditar={movimientoAEditar?.tipo === 'gasto' ? movimientoAEditar : null}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-300 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">Panel Financiero</h1>
        <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-6 border-b border-gray-700 pb-4">
            <div className="flex flex-wrap gap-3">
                <div>
                    <label htmlFor="startDate" className="text-sm text-gray-400">Desde</label>
                    <input
                        type="date"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-900 text-gray-300"
                    />
                </div>
                <div>
                    <label htmlFor="endDate" className="text-sm text-gray-400">Hasta</label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-900 text-gray-300"
                    />
                </div>
            </div>
        </div>
        <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-6 border-b border-gray-700 pb-4">
          <NavButton id="ingresos" label="Ingresos" icon={TrendingUp} />
          <NavButton id="gastos" label="Gastos" icon={TrendingDown} />
        </div>
        {errorApi && (
          <div className="bg-red-500/20 p-4 rounded-lg text-red-300 mb-6">
            {errorApi}
          </div>
        )}
        {currentSection === 'ingresos' && <IngresosSection />}
        {currentSection === 'gastos' && <GastosSection />}
      </div>
      <ModalCrearIngreso 
        isOpen={isIngresoModalOpen} 
        onClose={handleCloseModal} 
        onIngresoCreado={cargarIngresos}
        ingresoAEditar={movimientoAEditar?.tipo === 'ingreso' ? movimientoAEditar : null}
      />
    </div>
  );
};

export default FinancialApp;
